import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { OnboardingState, OnboardingStatus } from '@/types/onboarding';
import { onboardingService } from '@/services/onboardingService';
import { userService } from '@/services/userService';
import { ResponsiveOnboardingWizard } from '@/components/onboarding/ResponsiveOnboardingWizard';
import { useN8nStatus } from '@/hooks/use-n8n-status';
import { useOnboardingSteps } from '@/hooks/use-onboarding-steps';
import { Button } from '@/components/ui/button';
import { LoaderCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';
import { useOnboardingStatus } from '@/hooks/use-onboarding-status';

// Componente simples de tratamento de erro
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error, resetErrorBoundary: () => void }) => (
  <div className="p-4 bg-red-50 border border-red-200 rounded-md max-w-md mx-auto mt-8">
    <h3 className="text-lg font-medium text-red-800 mb-2">Erro no componente</h3>
    <p className="text-red-700">{error.message}</p>
    <button 
      onClick={resetErrorBoundary} 
      className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200"
    >
      Tentar novamente
    </button>
  </div>
);

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const n8nStatus = useN8nStatus();
  const { steps } = useOnboardingSteps();
  const mountedRef = useRef(true); // Para lidar com unmount
  const { markAsCompleted } = useOnboardingStatus();

  // Verificar se o onboarding foi concluído
  const isOnboardingComplete = useCallback((state: OnboardingState) => {
    return state.status === OnboardingStatus.COMPLETED || 
           (state.current_step >= steps.length - 1 && 
            state.steps_data?.['completion']?.completed);
  }, [steps.length]);

  // Função para redirecionar de forma simples
  const redirectToDashboard = useCallback(() => {
    console.log('Redirecionando para dashboard após conclusão');
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  // Função para determinar qual etapa deve ser mostrada
  const determineCorrectStep = useCallback((state: OnboardingState): number => {
    // Verificar se temos uma navegação direta do dashboard
    const hasDirectNavigation = localStorage.getItem('onboarding_direct_navigation') === 'true';
    const targetStep = localStorage.getItem('onboarding_target_step');
    
    // Se temos uma navegação direta, limpar os flags e retornar a etapa alvo
    if (hasDirectNavigation && targetStep) {
      // Limpar os flags para não interferir em navegações futuras
      localStorage.removeItem('onboarding_direct_navigation');
      localStorage.removeItem('onboarding_target_step');
      
      const directStep = parseInt(targetStep, 10);
      
      // Validar se o passo solicitado é válido
      if (!isNaN(directStep) && directStep >= 0 && directStep < steps.length) {
        console.log(`OnboardingPage: Navegação direta para a etapa ${directStep}`);
        return directStep;
      }
    }
    
    // Verificar o parâmetro da URL
    const stepParam = searchParams.get('step');
    let requestedStep: number;
    
    if (stepParam !== null) {
      // Se existe um parâmetro na URL, usar ele
      requestedStep = parseInt(stepParam, 10);
    } else {
      // Se não existe, usar o estado atual do onboarding
      requestedStep = state.current_step || 0;
    }
    
    // Se n8n não estiver configurado, força a primeira etapa
    if (!n8nStatus.isReady) {
      return 0; // Primeira etapa (boas-vindas)
    }

    // Validar se o passo solicitado é válido
    if (isNaN(requestedStep) || requestedStep < 0 || requestedStep >= steps.length) {
      return state.current_step;
    }

    return requestedStep;
  }, [n8nStatus.isReady, searchParams, steps.length]);

  // Cleanup no unmount para evitar memory leaks
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadOnboardingState = async () => {
      try {
        // Obter o usuário atual através do userService
        const user = await userService.getCurrentUser();
        
        if (!user) {
          console.error('OnboardingPage: Usuário não encontrado');
          navigate('/login');
          return;
        }
        
        // Usar o ID do usuário autenticado
        const state = await onboardingService.getOnboardingState(user.id);
        
        if (!mountedRef.current) return; // Se o componente foi desmontado, não continue
        
        if (!state) {
          console.log('OnboardingPage: Nenhum estado de onboarding encontrado, inicializando...');
          const newState = await onboardingService.initOnboardingState(user.id);
          if (mountedRef.current) {
            setOnboardingState(newState);
            setSearchParams({ step: '0' });
          }
          setIsLoading(false);
          return;
        }
        
        console.log('OnboardingPage: Estado de onboarding carregado:', state);
        console.log('OnboardingPage: Status do onboarding:', state.status);
        
        // Determinar o passo correto baseado nas regras de negócio
        const correctStep = determineCorrectStep(state);
        
        if (!mountedRef.current) return; // Se o componente foi desmontado, não continue
        
        // Atualizar o estado de onboarding (local e no servidor) se necessário
        if (correctStep !== state.current_step) {
          const updatedState = {
            ...state,
            current_step: correctStep
          };
          setOnboardingState(updatedState);
          
          // Sincronizar com o servidor
          await onboardingService.saveOnboardingState(updatedState);
          
          // Sincronizar o URL com o passo atual
          setSearchParams({ step: correctStep.toString() });
        } else {
          setOnboardingState(state);
          
          // Garantir que o URL está sincronizado
          setSearchParams({ step: state.current_step.toString() });
        }
      } catch (err) {
        console.error('OnboardingPage: Falha ao carregar estado de onboarding:', err);
        if (mountedRef.current) {
          setError('Não foi possível carregar os dados de onboarding');
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadOnboardingState();
  }, [location.pathname, navigate, searchParams, setSearchParams, determineCorrectStep, isOnboardingComplete]);

  // Efeito para atualizar a URL quando a etapa atual ou o status do n8n mudar
  useEffect(() => {
    if (onboardingState && mountedRef.current) {
      const currentStepParam = searchParams.get('step');
      const correctStep = determineCorrectStep(onboardingState);
      
      if (currentStepParam !== correctStep.toString()) {
        setSearchParams({ step: correctStep.toString() });
      }
    }
  }, [onboardingState, n8nStatus.isReady, searchParams, setSearchParams, determineCorrectStep]);

  const handleStepComplete = async (stepId: string, data?: any) => {
    if (!onboardingState || !mountedRef.current) return;
    
    try {
      console.log(`OnboardingPage: Completando etapa ${stepId} com dados:`, data);
      
      // Verificar se é uma solicitação apenas para salvar o progresso
      const isInternalSaveOnly = data && data.__internal_save_only === true;
      
      // Se for apenas para salvar, remover o flag antes de salvar no servidor
      if (isInternalSaveOnly && data.whatsapp) {
        delete data.__internal_save_only;
      }
      
      // Atualizar o estado no servidor
      await onboardingService.updateStep(onboardingState.user_id, stepId, data || {});
      
      if (!mountedRef.current) return; // Se o componente foi desmontado, não continue
      
      // Se for apenas para salvar o progresso, não avançar para a próxima etapa
      if (isInternalSaveOnly) {
        console.log(`OnboardingPage: Salvando progresso da etapa ${stepId} sem avançar`);
        
        // Atualizar apenas os dados da etapa no estado local
        const updatedOnboardingState = {
          ...onboardingState,
          steps_data: { 
            ...onboardingState.steps_data, 
            [stepId]: data 
          }
        };
        
        setOnboardingState(updatedOnboardingState);
        return;
      }
      
      // Verificar se o usuário está completando a etapa de conteúdo (etapa anterior à final)
      // e se estamos indo para a etapa de conclusão
      if (stepId === 'content') {
        try {
          console.log("OnboardingPage: Chamando n8n-provisioner para criar workspace e workflow antes da etapa final");
          
          // Obter a sessão atual para o token de autenticação
          const session = await supabase.auth.getSession();
          if (!session.data.session?.access_token) {
            console.error("OnboardingPage: Sessão não disponível para provisionar n8n");
          } else {
            // Chamar a função para provisionar o n8n (workspace e workflow)
            const { data: provisionData, error: provisionError } = await supabase.functions.invoke('n8n-provisioner', {
              body: { 
                action: "provision",
                userId: session.data.session.user.id
              },
              headers: {
                Authorization: `Bearer ${session.data.session.access_token}`
              }
            });
            
            if (provisionError) {
              console.error("OnboardingPage: Erro ao provisionar n8n:", provisionError);
            } else if (provisionData?.success) {
              console.log("OnboardingPage: Workspace n8n provisionado com sucesso:", provisionData.workspace);
              
              // Mostrar toast de sucesso ao usuário
              toast({
                title: "Ambiente preparado",
                description: "Seu ambiente de automação foi configurado com sucesso!",
                variant: "default",
              });
            }
          }
        } catch (provisionError) {
          console.error("OnboardingPage: Erro ao provisionar n8n:", provisionError);
          // Não vamos bloquear o fluxo de onboarding se o provisionamento falhar
          // O usuário ainda poderá tentar fazer isso na etapa final
        }
      }
      
      // Verificar se é o último passo para marcar como concluído
      const isLastStep = stepId === steps[steps.length - 1].id;
      let updatedOnboardingState: OnboardingState;
      
      if (isLastStep) {
        // Marcar onboarding como concluído
        updatedOnboardingState = {
          ...onboardingState,
          status: OnboardingStatus.COMPLETED,
          steps_data: { 
            ...onboardingState.steps_data, 
            [stepId]: { ...(data || {}), completed: true } 
          },
          completed_at: new Date().toISOString()
        };
        
        // Atualizar estado no servidor como concluído
        await onboardingService.completeOnboarding(onboardingState.user_id);
        
        // Também usar o novo hook para garantir redundância
        await markAsCompleted();
        
        if (!mountedRef.current) return; // Se o componente foi desmontado, não continue
        
        // Atualizar estado local
        setOnboardingState(updatedOnboardingState);
        
        // Sincronizar com o servidor
        await onboardingService.saveOnboardingState(updatedOnboardingState);
        
        // Garantir que o status é salvo no localStorage
        try {
          localStorage.setItem('onboarding_completed', 'true');
          localStorage.setItem('onboarding_user_id', onboardingState.user_id);
          localStorage.setItem('onboarding_completed_at', new Date().toISOString());
        } catch (error) {
          console.error('OnboardingPage: Erro ao salvar status no localStorage', error);
        }
        
        // Redirecionar para o dashboard após um breve intervalo
        console.log('OnboardingPage: Última etapa concluída, preparando redirecionamento para dashboard');
        
        // Usar um atraso para garantir que o salvamento foi concluído
        setTimeout(() => {
          if (mountedRef.current) {
            redirectToDashboard();
          }
        }, 1500);
      } else {
        // Determinar o próximo passo baseado no stepId atual e status do n8n
        let nextStep: number;
        
        // Se o n8n não está configurado, manter na primeira etapa
        if (!n8nStatus.isReady && stepId === 'welcome') {
          nextStep = 0; // Manter na etapa de boas-vindas
        } else {
          // Navegação normal entre etapas
          nextStep = getNextStepNumber(stepId);
        }
        
        // Atualizar o estado local
        updatedOnboardingState = { 
          ...onboardingState,
          current_step: nextStep,
          steps_data: { ...onboardingState.steps_data, [stepId]: data || {} }
        };
        
        if (!mountedRef.current) return; // Se o componente foi desmontado, não continue
        
        // Atualizar estado local
        setOnboardingState(updatedOnboardingState);
        
        // Sincronizar com o servidor
        await onboardingService.saveOnboardingState(updatedOnboardingState);
        
        // Atualizar o URL
        setSearchParams({ step: updatedOnboardingState.current_step.toString() });
      }
    } catch (err) {
      console.error('OnboardingPage: Falha ao atualizar estado de onboarding:', err);
      if (mountedRef.current) {
        setError('Não foi possível salvar o progresso do onboarding');
      }
    }
  };

  // Função auxiliar para determinar o próximo passo como número
  const getNextStepNumber = (currentStepId: string): number => {
    const stepOrder = steps.map(step => step.id);
    const currentIndex = stepOrder.indexOf(currentStepId);
    return (currentIndex < stepOrder.length - 1) ? currentIndex + 1 : stepOrder.length - 1;
  };

  // Função para navegar para um passo específico (permitir navegação livre após n8n configurado)
  const handleNavigateToStep = useCallback((stepNumber: number) => {
    console.log("OnboardingPage: handleNavigateToStep chamado com stepNumber:", stepNumber);
    
    if (!onboardingState || !mountedRef.current) {
      console.log("OnboardingPage: Não é possível navegar - onboardingState não existe ou componente foi desmontado");
      return;
    }
    
    // Validar o número do passo
    const validStep = Math.max(0, Math.min(stepNumber, steps.length - 1));
    console.log("OnboardingPage: Número do passo validado:", validStep);
    
    // Atualizar estado e URL
    const updatedState = {
      ...onboardingState,
      current_step: validStep
    };
    
    console.log("OnboardingPage: Atualizando estado com o novo passo:", updatedState.current_step);
    setOnboardingState(updatedState);
    setSearchParams({ step: validStep.toString() });
    
    // Sincronizar com o servidor
    onboardingService.saveOnboardingState(updatedState).catch(err => {
      console.error('OnboardingPage: Erro ao salvar estado de onboarding:', err);
    });
  }, [onboardingState, mountedRef, steps.length, setSearchParams]);

  // Adicionar um banner quando o onboarding já foi concluído
  const renderCompletionBanner = () => {
    if (onboardingState && isOnboardingComplete(onboardingState)) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6 mx-auto max-w-4xl">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h3 className="text-sm font-medium text-green-800">
              Seu onboarding já foi concluído
            </h3>
          </div>
          <p className="text-sm text-green-700 mt-2">
            Você está revisando suas configurações. Todas as alterações serão salvas automaticamente.
          </p>
          <div className="mt-3">
            <Button 
              variant="outline" 
              size="sm"
              className="text-green-700 hover:bg-green-100"
              onClick={redirectToDashboard}
            >
              Voltar para o Dashboard
            </Button>
          </div>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <LoaderCircle className="h-10 w-10 animate-spin text-primary mb-4" />
        <p>Carregando dados de onboarding...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar dados</AlertTitle>
          <AlertDescription>
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2" 
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!onboardingState) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Onboarding não encontrado</AlertTitle>
          <AlertDescription>
            Não foi possível encontrar os dados de onboarding. Tente fazer login novamente.
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2" 
              onClick={() => navigate('/login')}
            >
              Ir para Login
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <>
      {/* Banner de conclusão visível apenas em tamanhos maiores */}
      <div className="hidden sm:block">
        {renderCompletionBanner()}
      </div>
      
      {/* Componente de onboarding responsivo */}
      {onboardingState && (
        <ResponsiveOnboardingWizard
          userId={onboardingState.user_id}
          currentState={onboardingState}
          onStepComplete={handleStepComplete}
          onNavigateToStep={handleNavigateToStep}
          allowNavigation={true} // Permitir navegação sempre para testar
        />
      )}
    </>
  );
};

export default OnboardingPage; 